/**
 * VersionTree.jsx — Renders the design generation history as a
 * branching tree instead of a linear timeline.
 *
 * The tree is built from `versions[]` where each entry may have a
 * `parent_version` field:
 *   0 = originating from the root (v0 / the initial generation)
 *   N (1..len) = originating from versions[N-1]
 *   undefined  = linear fallback (parent = previous index) for legacy docs
 *
 * We compute (x, y) grid positions with a simple layered layout:
 *   - x = depth from root
 *   - y = a stable index among siblings, spread vertically
 * Each node is clickable → jumps to that version. Current node is
 * outlined in accent orange. Sibling branches are connected via
 * curved SVG paths for visual continuity.
 */
import { useMemo } from 'react';
import { Undo2, Redo2 } from 'lucide-react';

const NODE_W = 44;
const NODE_H = 22;
const COL_GAP = 18;   // px between depth columns
const ROW_GAP = 10;   // px between siblings vertically

/**
 * Build the tree structure + assign layout coordinates.
 * versions is an array of {version, parent_version, prompt}. Returns
 * a flat list of nodes with x/y + a list of edges (parent → child).
 */
const buildLayout = (versions) => {
  // Nodes indexed by version number. 0 = root.
  const nodes = [{ version: 0, parent: null, depth: 0, label: 'v0' }];
  versions.forEach((v, i) => {
    const idx = i + 1;
    const parent = (typeof v.parent_version === 'number') ? v.parent_version : Math.max(0, idx - 1);
    nodes.push({ version: idx, parent, depth: 0, label: `v${idx}`, prompt: v.prompt });
  });
  // Depth = distance from root.
  const byVersion = Object.fromEntries(nodes.map((n) => [n.version, n]));
  nodes.forEach((n) => {
    if (n.version === 0) return;
    let d = 1;
    let cur = n;
    while (cur.parent !== null && byVersion[cur.parent] && byVersion[cur.parent].version !== 0) {
      d += 1;
      cur = byVersion[cur.parent];
      if (d > 50) break;   // cycle guard
    }
    n.depth = d;
  });
  // Layer nodes by depth, assign y within each layer sorted by version.
  const layers = {};
  nodes.forEach((n) => { (layers[n.depth] = layers[n.depth] || []).push(n); });
  Object.values(layers).forEach((layer) => layer.sort((a, b) => a.version - b.version));
  Object.entries(layers).forEach(([depth, layer]) => {
    layer.forEach((n, i) => {
      n.x = 6 + Number(depth) * (NODE_W + COL_GAP);
      n.y = 6 + i * (NODE_H + ROW_GAP);
    });
  });
  const edges = nodes
    .filter((n) => n.version > 0)
    .map((n) => ({ from: byVersion[n.parent], to: n }));
  const totalW = Math.max(...nodes.map((n) => n.x + NODE_W)) + 8;
  const totalH = Math.max(...nodes.map((n) => n.y + NODE_H)) + 8;
  return { nodes, edges, totalW, totalH };
};

export const VersionTree = ({ versions, currentVersion, onJump, onUndo, onRedo }) => {
  const { nodes, edges, totalW, totalH } = useMemo(() => buildLayout(versions || []), [versions]);
  const total = (versions || []).length;

  return (
    <div className="mb-3 flex items-start gap-1.5 flex-wrap" data-testid="ds-versions">
      <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold mr-1 mt-1">Versions:</span>
      <button onClick={onUndo} disabled={currentVersion === 0}
        className="px-2 py-1 rounded text-[10.5px] font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed mt-0.5"
        style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)', color: 'var(--text-primary)' }}
        title="Undo (Ctrl+Z)" data-testid="ds-undo">
        <Undo2 size={11} />
      </button>
      <button onClick={onRedo} disabled={currentVersion >= total}
        className="px-2 py-1 rounded text-[10.5px] font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed mt-0.5"
        style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)', color: 'var(--text-primary)' }}
        title="Redo (Ctrl+Shift+Z)" data-testid="ds-redo">
        <Redo2 size={11} />
      </button>
      <div className="w-px h-4 mx-1 mt-1" style={{ background: 'var(--hairline)' }} />

      <div className="relative overflow-x-auto" style={{ maxWidth: '100%' }}>
        <svg width={totalW} height={totalH} style={{ minWidth: totalW, display: 'block' }} data-testid="ds-version-tree">
          {edges.map((e, i) => {
            // Cubic bezier from right-middle of parent to left-middle of child
            const x1 = e.from.x + NODE_W;
            const y1 = e.from.y + NODE_H / 2;
            const x2 = e.to.x;
            const y2 = e.to.y + NODE_H / 2;
            const mx = (x1 + x2) / 2;
            return (
              <path key={i}
                d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
                stroke="var(--hairline)" strokeWidth={1.2} fill="none" />
            );
          })}
          {nodes.map((n) => {
            const isCurrent = n.version === currentVersion;
            return (
              <g key={n.version}
                onClick={() => onJump(n.version)}
                style={{ cursor: 'pointer' }}
                data-testid={`ds-version-${n.version}`}>
                <rect x={n.x} y={n.y} width={NODE_W} height={NODE_H} rx={4}
                  fill={isCurrent ? 'rgba(242,107,31,0.15)' : 'var(--layer-1)'}
                  stroke={isCurrent ? '#F26B1F' : 'var(--hairline)'}
                  strokeWidth={isCurrent ? 1.5 : 1} />
                <text x={n.x + NODE_W / 2} y={n.y + NODE_H / 2 + 3.5}
                  textAnchor="middle" fontSize={10.5} fontWeight={600}
                  fill={isCurrent ? '#F26B1F' : 'var(--text-primary)'}
                  fontFamily="Inter, sans-serif" pointerEvents="none">
                  {n.label}
                </text>
                {/* Native tooltip for the prompt so users can hover to preview */}
                {n.prompt && <title>{n.prompt}</title>}
              </g>
            );
          })}
        </svg>
      </div>

      <span className="text-[10px] text-nexus-text-tertiary ml-auto mt-1" data-testid="ds-current-version-label">
        on v{currentVersion} of {total}
      </span>
    </div>
  );
};

export default VersionTree;
